import type { AxiosRequestConfig } from 'axios'
import { serviceGo } from '@/utils/request'

/** 素材类型：图片四类 + 音频两类，与后端 db.AssetKinds 一致 */
export type AssetKind = 'bg' | 'portrait' | 'avatar' | 'item' | 'bgm' | 'sfx'

/** 库范围：官方（user_id = -1）/ 个人（当前用户）/ 本书（该书的共享素材库，需 game_id，
    凡能进这本书的人都能读写）/ 指定账号（user，仅管理员，只读，需带 user_id——A 计划「个人素材管理」） */
export type AssetScope = 'official' | 'mine' | 'book' | 'user'

/** 与 Go 的 db.Asset 逐字段对应（IsDeleted 标了 json:"-" 不下发） */
export interface Asset {
  id: number
  user_id: number
  game_id?: number | null // 非空 = 本书素材库（该书的共享库）；此时 user_id 是上传者、不参与权限
  kind: AssetKind
  name: string
  url: string
  oss_filename: string
  file_size: number
  duration_sec: number | null // 只有音频有
  width: number | null
  height: number | null
  category_id: number | null // null = 未分类
  created_at: string
  updated_at: string
}

/**
 * 素材展示所需的最小字段集（A 计划终审修复 F1）。
 *
 * 由来：`ChapterContentEditor.assetInfo` 这份共享缓存有**两个**来源，两边的形状并不相同——
 * ① `loadAssetInfo()` 整表拉 `/api_v2/assets` 拿到的是完整 `Asset`；② 章节内容 GET 顺带
 * 带回的 `data.assets`（A 计划 §4.1）是 `AssetBrief`（只有 id/name/url/kind/is_deleted）。
 * 缓存的值类型因此收窄到"消费方真正读的字段"这个交集：`MountTags` 读 `url` 画缩略图、
 * `mountLabels.assetNameOf` 与两个挂载弹窗读 `name` 显示文件名，没有任何消费方读
 * `file_size` / `duration_sec` / `category_id` 之类的列。给 brief 补一批假的默认值来凑满
 * `Asset` 是更坏的选择：那是**伪造数据**，日后谁真去读那些字段就会拿到编造的值。
 *
 * `kind` 收 `string` 而不是 `AssetKind`：`AssetBrief.kind` 在 Go 侧就是裸 string，两种来源
 * 都能赋值给本类型（`Asset` 的 `AssetKind` 是 `string` 的子类型）。
 */
export interface AssetDisplay {
  id: number
  name: string
  url: string
  kind: string
}

/** 列表接口里的分类视图（比裸分类多一个 count） */
export interface AssetCategoryView {
  id: number
  parent_id?: number | null // 缺省 = 老后端（当顶级）；null = 顶级；数字 = 父分组（spec §6.1、§7）
  game_id?: number | null // 非空 = 本书素材库的分组；目前列表接口的分类视图（assetCategoryView）实际未下发此列，仅为类型对齐预留
  name: string
  sort: number
  count: number
}

/**
 * 分类接口返回的裸分类，对应 Go 的 db.AssetCategory。
 * created_at 由 gorm 自动维护并以 json:"created_at" 下发（listAssetCategories /
 * createAssetCategory / updateAssetCategory 都会带上），简报代码遗漏了这个字段，这里按源码补上。
 */
export interface AssetCategory {
  id: number
  user_id: number
  game_id?: number | null // 非空 = 本书素材库的分组；此时 user_id 是建者、不参与权限
  kind: AssetKind
  parent_id?: number | null // 缺省 = 老后端（当顶级）；null = 顶级；数字 = 父分组（spec §6.1、§7）
  name: string
  sort: number
  created_at: string
}

/** GET /api_v2/assets 的 data。分类计数与总数不受 q / category_id 影响 */
export interface AssetListData {
  items: Asset[]
  total: number
  page: number
  size: number
  categories: AssetCategoryView[]
  uncategorized_count: number
  total_all: number
  /** 本页出现过的素材上传者用户名，key 是 user_id 的字符串形式（Go 侧 strconv.FormatInt）。
      只有 scope === 'book' 时后端才会解析、下发非空映射；其余 scope 恒为空对象。
      查不到用户名的 id 不会出现在映射里，前端兜底显示「用户#<id>」 */
  uploaders?: Record<string, string>
}

export interface AssetListParams {
  kind: AssetKind
  scope: AssetScope
  /** scope === 'book' 时必填：本书 id，后端据此解析该书的共享素材库 */
  game_id?: number
  /** scope === 'user' 时必填：要查看的账号 id（仅管理员，后端非管理员 403） */
  user_id?: number
  /** 0 = 只看未分类；不传 = 全部 */
  category_id?: number
  q?: string
  page?: number
  /** 默认 40，后端上限 200 */
  size?: number
}

/**
 * `config` 可传 `{ _silentError: true }` 跳过全局兜底弹窗（照 `listVariables` 的既有写法）——
 * 计划 05b Task 3 建的共享素材缓存（`ChapterContentEditor.loadAssetInfo`）在 `created()`
 * 里不 await 地整表拉取，失败时只能静默降级，不该弹模态框打断作者。既有调用方
 * （`AssetLibrary.fetch()`）不传第二个参数，行为不变。
 */
export function listAssets(params: AssetListParams, config?: AxiosRequestConfig & { _silentError?: boolean }) {
  return serviceGo.get('/api_v2/assets', { params, ...config })
}

/** 「个人素材管理」账号下拉的一项（GET /api_v2/admin/assets/owners） */
export interface AssetOwner {
  user_id: number
  username: string
  count: number
}

/** 有该类未删个人素材的账号列表（不含官方），按数量降序；仅管理员（AdminAuth，非管理员 403）。
    返回原始 axios 响应，`res.data.data.items` 是 `AssetOwner[]`——同本文件其余函数不拆包的约定 */
export function listAssetOwners(kind: AssetKind) {
  return serviceGo.get('/api_v2/admin/assets/owners', { params: { kind } })
}

/**
 * 上传素材。
 * - official 只有 admin 能传 true；非 admin 传 true 会被后端 403
 * - 音频（bgm / sfx）必须带 durationSec，后端校验 0 < d <= 3600
 */
export function uploadAsset(opts: {
  file: File
  kind: AssetKind
  name?: string
  official?: boolean
  durationSec?: number
  width?: number
  height?: number
  /** 上传直接落到这个分组（作者定案 2026-09-18）。不传 = 进未分类；
      传了则后端要求它是本库本类型的**叶子**分组，否则按「只能移到没有子分组的分组」拒绝 */
  categoryId?: number | null
  /** 传本书 id（>0）= 上传进本书素材库，后端按此解析归属并做书级鉴权；
      null / undefined = 不带该字段，沿用 official 的官方/个人二选一（旧行为不变） */
  gameId?: number | null
}, config?: AxiosRequestConfig & { _silentError?: boolean }) {
  const fd = new FormData()
  fd.append('file', opts.file)
  fd.append('kind', opts.kind)
  if (opts.name) fd.append('name', opts.name)
  if (opts.official !== undefined) fd.append('official', opts.official ? '1' : '0')
  if (opts.durationSec !== undefined) fd.append('duration_sec', String(opts.durationSec))
  if (opts.width !== undefined) fd.append('width', String(opts.width))
  if (opts.height !== undefined) fd.append('height', String(opts.height))
  // null / undefined 都不带这个字段，后端据此走「进未分类」的旧行为
  if (opts.categoryId !== undefined && opts.categoryId !== null) fd.append('category_id', String(opts.categoryId))
  if (opts.gameId !== undefined && opts.gameId !== null) fd.append('game_id', String(opts.gameId))
  return serviceGo.post('/api_v2/assets/upload', fd, {
    ...config,
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

/** 改名 / 移动分类。category_id 传 null = 移到未分类。官方素材仅 admin 可改 */
export function updateAsset(id: number, data: { name?: string; category_id?: number | null }) {
  return serviceGo.put(`/api_v2/assets/${id}`, data)
}

/** 删除。被引用时返回 code 6004「素材已被使用，不可删除」，要把 message 原样弹给作者 */
export function deleteAsset(id: number) {
  return serviceGo.delete(`/api_v2/assets/${id}`)
}

export interface AssetBatchSkipped {
  id: number
  reason: string
}

/** affected + skipped.length === 请求里去重后的 ids 长度 */
export interface AssetBatchData {
  affected: number
  skipped: AssetBatchSkipped[]
}

/**
 * 批量移动 / 批量删除。
 * 后端会对 ids 去重；批内素材须同 owner 且同 kind，move 的目标分类也须同 owner 同 kind。
 */
export function batchAssets(data: {
  ids: number[]
  action: 'move' | 'delete'
  category_id?: number | null
}) {
  return serviceGo.post('/api_v2/assets/batch', data)
}

// ---------- 分类 ----------

/** scope === 'book' 时 gameId 必填，透传为 game_id；其余 scope 不传（undefined 时 axios 不带该参数） */
export function listAssetCategories(kind: AssetKind, scope: AssetScope, gameId?: number) {
  return serviceGo.get('/api_v2/asset-categories', { params: { kind, scope, game_id: gameId } })
}

/** official 未传时后端按角色决定：admin 默认官方库，其他人归个人。parent_id 缺省 / null = 顶级；
    父分组原本是放着素材的叶子时，那些素材会随之移入新分组（后端事务内做，spec §5.2）。
    scope === 'book' 时 gameId 必填，走本书素材库（书级鉴权）；scope 缺省时沿用 official 二选一（旧行为不变） */
export function createAssetCategory(data: { kind: AssetKind; name: string; official?: boolean; parent_id?: number | null; scope?: AssetScope; gameId?: number | null }) {
  return serviceGo.post('/api_v2/asset-categories', {
    kind: data.kind,
    name: data.name,
    official: data.official,
    parent_id: data.parent_id,
    scope: data.scope,
    game_id: data.gameId
  })
}

/** 改名 / 换父二选一或同时：parent_id 缺省 = 不换父，null = 移回顶级。后端可能拒绝：成环 / 超过 5 级 /
    目标分组直接放着素材，把 message 原样弹出即可（spec §5.3） */
export function updateAssetCategory(id: number, data: { name?: string; parent_id?: number | null }) {
  return serviceGo.put(`/api_v2/asset-categories/${id}`, data)
}

export function deleteAssetCategory(id: number) {
  return serviceGo.delete(`/api_v2/asset-categories/${id}`)
}

/** 拖动排序：整组 id 按新顺序发送，后端按数组下标重写 sort（W9）。
    scope === 'book' 时 gameId 必填，走本书素材库（书级鉴权）；scope 缺省时沿用 official 二选一（旧行为不变） */
export function reorderAssetCategories(data: { kind: AssetKind; ids: number[]; official?: boolean; scope?: AssetScope; gameId?: number | null }) {
  return serviceGo.post('/api_v2/asset-categories/reorder', {
    kind: data.kind,
    ids: data.ids,
    official: data.official,
    scope: data.scope,
    game_id: data.gameId
  })
}
